import { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
    Zap,
    Users,
    Target,
    Award,
    Shield,
    Camera,
    Brain,
    Eye,
    Home,
    Mail,
    Menu,
    HelpCircle,
    LogIn,
    DollarSign,
    ChevronRight,
    Globe
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { BrandMark } from './BrandMark';

interface AboutUsProps {
    onNavigate: (page: 'home' | 'about' | 'contact' | 'pricing') => void;
    onShowAuth: () => void;
    onShowSupport: () => void;
}

const GlassCard: React.FC<{ children: React.ReactNode; className?: string; hover?: boolean }> = ({ children, className = '', hover = true }) => (
    <div className={`bg-white/5 backdrop-blur-3xl border border-white/10 rounded-3xl p-8 transition-all duration-500 overflow-hidden relative ${hover ? 'hover:bg-white/10 hover:border-white/20 hover:-translate-y-2 hover:shadow-[0_0_40px_rgba(59,130,246,0.1)] group' : ''} ${className}`}>
        {children}
    </div>
);

export function AboutUs({ onNavigate, onShowAuth, onShowSupport }: AboutUsProps) {
    const [showMobileMenu, setShowMobileMenu] = useState(false);

    const teamMembers = [
        {
            name: "AI Vision Team",
            role: "Computer Vision Engineers",
            description: "Developing cutting-edge AI algorithms for intelligent surveillance using YOLO, OpenCV, and specialized neural nets.",
            icon: Eye,
            color: "text-blue-400",
            bgColor: "bg-blue-500/10",
            borderColor: "border-blue-500/20"
        },
        {
            name: "Security Experts",
            role: "Security Specialists",
            description: "Ensuring robust protection and privacy in all our solutions with enterprise-grade security protocols.",
            icon: Shield,
            color: "text-emerald-400",
            bgColor: "bg-emerald-500/10",
            borderColor: "border-emerald-500/20"
        },
        {
            name: "Product Team",
            role: "Platform Architecture",
            description: "Creating highly immersive glassmorphic interfaces and seamless tactical experiences.",
            icon: Users,
            color: "text-purple-400",
            bgColor: "bg-purple-500/10",
            borderColor: "border-purple-500/20"
        },
        {
            name: "Research Lab",
            role: "AI Research Scientists",
            description: "Advancing the state-of-the-art in predictive analysis and real-time edge processing.",
            icon: Brain,
            color: "text-yellow-400",
            bgColor: "bg-yellow-500/10",
            borderColor: "border-yellow-500/20"
        }
    ];

    const achievements = [
        {
            title: "99.9% Precision",
            description: "Industry-leading threat detection accuracy across global surveillance grids.",
            icon: Target,
            detail: "Validated on 50M+ test matrices",
            color: "text-blue-400"
        },
        {
            title: "Zero Downtime",
            description: "Perpetual cognitive monitoring designed for continuous operational capability.",
            icon: Camera,
            detail: "1M+ runtime hours",
            color: "text-emerald-400"
        },
        {
            title: "Instant Triage",
            description: "Sub-millisecond localized threat evaluation and instant routing protocols.",
            icon: Zap,
            detail: "< 50ms latency",
            color: "text-amber-400"
        },
        {
            title: "Award Winning",
            description: "Recognized as the apex standard in automated tactical surveillance.",
            icon: Award,
            detail: "Defense Tech Innovator 2025",
            color: "text-purple-400"
        },
        {
            title: "Global Reach",
            description: "Active deployment across high-stakes environments globally.",
            icon: Globe,
            detail: "25+ Countries Protected",
            color: "text-indigo-400"
        },
        {
            title: "Enterprise Grade",
            description: "Infinite scaling architecture for sprawling physical security meshes.",
            icon: Users,
            detail: "1 to 10k+ linked nodes",
            color: "text-rose-400"
        }
    ];

    return (
        <div className="min-h-screen bg-[#0a0b14] text-white flex flex-col relative overflow-hidden">
            {/* Ambient Backgrounds */}
            <div className="fixed inset-0 z-0 pointer-events-none">
               <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-500/5 blur-[120px] rounded-full -mr-96 -mt-96" />
               <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-500/5 blur-[100px] rounded-full -ml-64 -mb-64" />
               <div className="absolute top-1/2 left-1/2 w-full h-[2px] bg-white/[0.02] rotate-12" />
               <div className="absolute top-1/2 left-1/2 w-full h-[2px] bg-white/[0.02] -rotate-12" />
            </div>

            {/* Premium Navbar */}
            <nav className="sticky top-0 z-50 bg-[#0a0b14]/80 backdrop-blur-2xl border-b border-white/5 px-6 py-4">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-4 group cursor-pointer" onClick={() => onNavigate('home')}>
                        <BrandMark className="w-[86px] h-[48px] group-hover:scale-105 transition-transform duration-500 drop-shadow-[0_0_14px_rgba(59,130,246,0.45)]" />
                        <div className="hidden sm:block">
                            <p className="text-2xl font-black tracking-[-0.02em] text-blue-100/95 leading-none">3RDI</p>
                            <p className="text-[10px] uppercase tracking-[0.24em] text-blue-100/45 mt-1 font-semibold">Vision Intelligence Platform</p>
                        </div>
                    </div>

                    <div className="hidden md:flex items-center gap-2">
                        <Button variant="ghost" onClick={() => onNavigate('home')} className="text-blue-100/60 hover:text-white hover:bg-white/5 transition-all px-4 py-2 rounded-xl">
                            <Home className="h-4 w-4 mr-2" /> Home
                        </Button>
                        <Button variant="ghost" className="bg-white/5 text-white px-4 py-2 rounded-xl">
                            <Users className="h-4 w-4 mr-2 text-blue-400" /> About
                        </Button>
                        <Button variant="ghost" onClick={() => onNavigate('contact')} className="text-blue-100/60 hover:text-white hover:bg-white/5 transition-all px-4 py-2 rounded-xl">
                            <Mail className="h-4 w-4 mr-2" /> Contact
                        </Button>
                        <Button variant="ghost" onClick={() => onNavigate('pricing')} className="text-blue-100/60 hover:text-white hover:bg-white/5 transition-all px-4 py-2 rounded-xl">
                            <DollarSign className="h-4 w-4 mr-2" /> Pricing
                        </Button>
                        <div className="h-6 w-[1px] bg-white/10 mx-2" />
                        <Button variant="ghost" onClick={onShowSupport} className="text-blue-100/60 hover:text-white px-4 hover:bg-white/5 rounded-xl">Support</Button>
                        <button
                            onClick={onShowAuth}
                            className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2.5 rounded-xl font-bold text-sm transition-all shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:scale-105 active:scale-95"
                        >
                            <span className="flex items-center gap-2"><LogIn className="w-4 h-4" /> Sign In</span>
                        </button>
                    </div>

                    <button onClick={() => setShowMobileMenu(!showMobileMenu)} className="md:hidden p-2 text-gray-400 hover:bg-white/5 rounded-xl transition-colors">
                        <Menu />
                    </button>
                </div>
            </nav>

            <AnimatePresence>
                {showMobileMenu && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="fixed inset-x-0 top-[73px] z-40 bg-[#0a0b14]/95 backdrop-blur-3xl border-b border-white/10 p-6 md:hidden flex flex-col gap-4"
                    >
                        <button onClick={() => onNavigate('home')} className="flex items-center gap-4 text-blue-100/60 p-4 rounded-xl hover:bg-white/5 transition-all"><Home className="w-5 h-5"/> Home</button>
                        <button className="flex items-center gap-4 text-white bg-white/5 p-4 rounded-xl transition-all"><Users className="w-5 h-5 text-blue-400"/> About Us</button>
                        <button onClick={() => onNavigate('contact')} className="flex items-center gap-4 text-blue-100/60 p-4 rounded-xl hover:bg-white/5 transition-all"><Mail className="w-5 h-5"/> Contact</button>
                        <button onClick={() => onNavigate('pricing')} className="flex items-center gap-4 text-blue-100/60 p-4 rounded-xl hover:bg-white/5 transition-all"><DollarSign className="w-5 h-5"/> Pricing</button>
                        <hr className="border-white/5 my-2" />
                        <button onClick={onShowSupport} className="flex items-center gap-4 text-blue-100/60 p-4 rounded-xl hover:bg-white/5 transition-all"><HelpCircle className="w-5 h-5"/> Support</button>
                        <button onClick={onShowAuth} className="w-full bg-blue-600 py-4 rounded-2xl font-bold mt-4 flex justify-center items-center gap-2"><LogIn className="w-5 h-5" /> Login / Sign Up</button>
                    </motion.div>
                )}
            </AnimatePresence>

            <main className="flex-1 relative z-10 px-6 py-24 pb-32">
                <div className="max-w-7xl mx-auto space-y-32">
                    
                    {/* Hero Section */}
                    <div className="text-center space-y-8 max-w-4xl mx-auto">
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center justify-center p-4 rounded-3xl bg-blue-500/10 border border-blue-500/20 mb-4 shadow-[0_0_40px_rgba(59,130,246,0.3)]">
                            <Brain className="w-12 h-12 text-blue-400" />
                        </motion.div>
                        <h1 className="text-5xl md:text-8xl font-black tracking-tighter text-white">
                            THE <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">MIND</span> BEHIND THE MACHINE
                        </h1>
                        <p className="text-xl md:text-2xl text-blue-100/60 font-light leading-relaxed">
                            3RDI represents the total convergence of cognitive threat analysis, real-time edge processing, and autonomous defense architecture.
                        </p>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-12 border-t border-white/5">
                            <div className="text-center">
                                <div className="text-5xl font-black font-mono text-white mb-2 tracking-tighter">50+</div>
                                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-100/40">Enterprise Grids</div>
                            </div>
                            <div className="text-center">
                                <div className="text-5xl font-black font-mono text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400 mb-2 tracking-tighter">99.9%</div>
                                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-100/40">SLA Availability</div>
                            </div>
                            <div className="text-center">
                                <div className="text-5xl font-black font-mono text-white mb-2 tracking-tighter">24/7</div>
                                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-100/40">Neural Support</div>
                            </div>
                        </div>
                    </div>

                    {/* Mission Directive */}
                    <GlassCard hover={false} className="p-12 md:p-16">
                         <div className="flex flex-col md:flex-row gap-12 items-center">
                            <div className="w-full md:w-1/3">
                               <div className="w-24 h-24 rounded-3xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-6">
                                  <Target className="w-10 h-10 text-blue-400 drop-shadow-[0_0_15px_rgba(96,165,250,0.5)]" />
                               </div>
                               <h2 className="text-4xl font-black tracking-tighter uppercase mb-4">Core<br/>Directive</h2>
                               <p className="text-xs font-bold uppercase tracking-widest text-blue-400">Democratizing tactical intelligence.</p>
                            </div>
                            <div className="w-full md:w-2/3 space-y-8">
                                <p className="text-xl md:text-2xl text-blue-100/60 font-light leading-relaxed">
                                    We architect intelligence matrices that don't just passively record—they <span className="text-white font-medium">perceive, predict, and protect</span>. By bridging advanced computer vision with lethal edge computing, we forge environments where physical security is absolute and automated.
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6 group">
                                        <h4 className="text-white font-black uppercase tracking-widest mb-2 group-hover:text-blue-400 transition-colors">Perception</h4>
                                        <p className="text-xs text-blue-100/40 font-light leading-relaxed">Zero-blindspot automated detection networks.</p>
                                    </div>
                                    <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6 group">
                                        <h4 className="text-white font-black uppercase tracking-widest mb-2 group-hover:text-emerald-400 transition-colors">Cognition</h4>
                                        <p className="text-xs text-blue-100/40 font-light leading-relaxed">Deep learning prediction of hostile intent.</p>
                                    </div>
                                    <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6 group">
                                        <h4 className="text-white font-black uppercase tracking-widest mb-2 group-hover:text-purple-400 transition-colors">Action</h4>
                                        <p className="text-xs text-blue-100/40 font-light leading-relaxed">Immediate, automated perimeter mitigation.</p>
                                    </div>
                                </div>
                            </div>
                         </div>
                    </GlassCard>

                    {/* Operational Advantages */}
                    <div>
                        <h2 className="text-4xl md:text-5xl text-center font-black uppercase tracking-tighter mb-16">Operational Advantages</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {achievements.map((item, idx) => (
                                <GlassCard key={idx} className="flex flex-col items-center text-center">
                                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-8 border border-white/10 group-hover:scale-110 transition-transform ${item.color.replace('text-', 'bg-').replace('-400', '-500/10')}`}>
                                        <item.icon className={`w-8 h-8 ${item.color}`} />
                                    </div>
                                    <h3 className="text-2xl font-black uppercase tracking-tight mb-4">{item.title}</h3>
                                    <p className="text-blue-100/60 font-light leading-relaxed flex-1 mb-6">
                                        {item.description}
                                    </p>
                                    <Badge variant="outline" className={`bg-transparent border-white/10 ${item.color} text-[9px] font-black uppercase tracking-widest px-3 py-1`}>
                                        {item.detail}
                                    </Badge>
                                </GlassCard>
                            ))}
                        </div>
                    </div>

                    {/* Team Architecture */}
                    <GlassCard hover={false} className="p-0 border-0 bg-transparent shadow-none">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tighter mb-4">The Architects</h2>
                            <p className="text-xl text-blue-100/40 font-light max-w-2xl mx-auto">
                                The elite divisions maintaining and advancing the 3RDI global neural net.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {teamMembers.map((div, idx) => (
                                <div key={idx} className="bg-white/5 backdrop-blur-3xl border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-all duration-300">
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-6">
                                        <div className={`w-14 h-14 rounded-2xl border flex items-center justify-center shadow-lg bg-black/40 ${div.borderColor}`}>
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${div.bgColor}`}>
                                                <div.icon className={`w-5 h-5 ${div.color}`} />
                                            </div>
                                        </div>
                                        <div>
                                            <h3 className="text-2xl font-black uppercase tracking-tight text-white mb-1">{div.name}</h3>
                                            <p className={`text-xs font-bold uppercase tracking-widest ${div.color}`}>{div.role}</p>
                                        </div>
                                    </div>
                                    <p className="text-blue-100/60 font-light leading-relaxed">
                                        {div.description}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </GlassCard>

                </div>
            </main>

            <footer className="py-12 border-t border-white/5 text-center mt-auto relative z-10 bg-black/40 backdrop-blur-xl">
                <p className="text-blue-100/20 text-xs font-black uppercase tracking-[0.3em]">
                    © 2026 3RDI VISION INTELLIGENCE
                </p>
                <p className="text-blue-100/10 text-[9px] font-black uppercase tracking-widest mt-2 hover:text-blue-500/50 transition-colors cursor-pointer">
                    POWERED BY BeAItle DEFENSE ALGORITHMS
                </p>
            </footer>
        </div>
    );
}

// Ensure dummy Globe component exists or rely on lucide-react (imported successfully)
// Assuming lucide-react Globe is perfectly fine.
